<script setup lang="ts">
import { UiAlertBox } from '@/components/ui/UiAlertBox/index.ts'
import { UiDialog } from '@/components/ui/UiDialog/index.ts'
import { UiDialogPage } from '@/components/ui/UiDialogPage/index.ts'
import type { ConfirmConfig } from '@/services/confirm/index.ts'
import { useVModel } from '@/utils/use-vue/index.ts'

export interface Props extends ConfirmConfig {
  isOpen: boolean
}

export type Emits = {
  'update:isOpen': [value: boolean]
  approve: []
  cancel: []
  /** Emits after dialog closing animation is fully done */
  didClose: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const isOpenModel = useVModel(props, 'isOpen', emit)

function takeAction(actionToTake: 'approve' | 'cancel'): void {
  isOpenModel.value = false
  switch (actionToTake) {
    case 'approve':
      emit('approve')
      break
    case 'cancel':
      emit('cancel')
      break
    default:
      break
  }
}
</script>

<template>
  <UiDialog
    v-model:isOpen="isOpenModel"
    @click-outside="takeAction('cancel')"
    @did-close="emit('didClose')"
  >
    <UiDialogPage
      :cancelButtonText="cancelText"
      :submitButtonText="approveText"
      :title="title"
      @cancel="takeAction('cancel')"
      @submit="takeAction('approve')"
    >
      <p v-if="body">{{ body }}</p>

      <div
        v-if="alerts?.length"
        class="flex flex-col gap-3"
      >
        <UiAlertBox
          v-for="(alert, i) in alerts ?? []"
          :key="i"
          :title="alert?.title"
          :type="alert.type"
        >
          {{ alert.message }}
        </UiAlertBox>
      </div>
    </UiDialogPage>
  </UiDialog>
</template>
